/**
 * Owner: Masa Bayu
 * File: demo-author-journey.ts
 * Date:Time: 2026-09-16 15:20
 * Description: Demo T6-E7 vertical author journey for settings UI.
 */

import {
  confirmOrderAfterSuccessfulPayment,
  createAuthorCapableAccount,
  createPendingOrderDraft,
  createPaymentAttempt,
  createPaymentIntent,
  createQxreadAuthorAccount,
  createQxreadSession,
  projectSaleRecordsFromConfirmedOrders,
  runAuthorIntegrationJourney,
  type QxreadAuthorIntegrationJourney,
  type QxreadCheckoutSession,
} from '@qx/qxread-core';

const DEMO_ACCOUNT_ID = 'qxrdacc_demo_author';
const DEMO_NOW = '2026-09-16T07:20:00.000Z';
const DEMO_PUB = 'qxrdpub_demo_store';

const demoAccount = createAuthorCapableAccount({
  accountId: DEMO_ACCOUNT_ID,
  displayName: 'Demo Author',
});
const demoSession = createQxreadSession({
  state: 'authenticated',
  accountId: DEMO_ACCOUNT_ID,
  issuedAt: DEMO_NOW,
});
const demoAuthor = createQxreadAuthorAccount({
  accountId: DEMO_ACCOUNT_ID,
  displayName: 'Demo Author',
  slug: 'demo-author',
  createdAt: DEMO_NOW,
  updatedAt: DEMO_NOW,
});
let demoJourney: QxreadAuthorIntegrationJourney | null = null;

function demoCheckout(): QxreadCheckoutSession {
  return Object.freeze({
    checkoutId: 'qxrdchk_demo_jny',
    cartId: 'qxrdcart_demo_jny',
    sessionKey: 'qxrdsess_demo_jny',
    state: 'ready' as const,
    totals: Object.freeze({
      currency: 'USD',
      sellerSubtotalMinorUnits: 1299,
      serviceFeeMinorUnits: 58,
      buyerTotalMinorUnits: 1357,
      affiliateContractMinorUnits: 65,
      referralContractMinorUnits: 65,
      rbsContractMinorUnits: 0,
      uniquePublicationCount: 1,
      ledgerLines: Object.freeze([]),
      attributions: Object.freeze([]),
    }),
    blockers: Object.freeze([] as string[]),
    createdAt: DEMO_NOW,
  });
}

export function resetDemoAuthorJourney(): void {
  demoJourney = null;
}

export function getDemoAuthorJourney(): QxreadAuthorIntegrationJourney | null {
  return demoJourney;
}

export function runDemoAuthorJourney(includeSales: boolean): string {
  const pending = createPendingOrderDraft(demoCheckout(), [
    {
      publicationId: DEMO_PUB,
      title: 'River Atlas',
      quantity: 1,
      sellerPriceMinorUnits: 1299,
      currency: 'USD',
    },
  ]);
  let saleRecords: ReturnType<typeof projectSaleRecordsFromConfirmedOrders> | undefined;
  if (includeSales) {
    const intent = createPaymentIntent(pending, { idempotencyKey: 'demo_jny_pay' });
    const attempt = createPaymentAttempt(intent);
    const succeeded = Object.freeze({
      ...attempt,
      state: 'succeeded' as const,
      updatedAt: DEMO_NOW,
    });
    const confirmed = confirmOrderAfterSuccessfulPayment(pending, succeeded, {
      confirmedAt: DEMO_NOW,
    });
    saleRecords = projectSaleRecordsFromConfirmedOrders({
      authorAccount: demoAuthor,
      authorOwnedPublicationIds: new Set([DEMO_PUB]),
      confirmedOrders: [confirmed],
      recordedAt: DEMO_NOW,
    });
  }

  const journeyInput = {
    session: demoSession,
    account: demoAccount,
    authorAccount: demoAuthor,
    publicationId: DEMO_PUB,
    readiness: {
      hasTitle: true as const,
      hasCover: true as const,
      hasMetadata: true as const,
      qxpRefBound: true as const,
      hasVersionLabel: true as const,
    },
    now: DEMO_NOW,
    ...(saleRecords !== undefined ? { saleRecords } : {}),
  };
  demoJourney = runAuthorIntegrationJourney(journeyInput);
  return `Journey ${demoJourney.state} · composesE1ThroughE6=${String(demoJourney.composesE1ThroughE6)} · hostsQxwMutation=${String(demoJourney.hostsQxwMutation)} · chromeModeIsSecurityAuthority=${String(demoJourney.chromeModeIsSecurityAuthority)}`;
}
